import { useState, useEffect } from "react"
import { PascosTable } from "../dashboard/components/PascosTable"
import { Search, CheckCircle2, RefreshCw, FileText } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useListPascosPaginated } from "@src/presentation/hooks/pasco"
import {
    Button,
    Card,
    CardBody,
    Input,
    Select,
    SelectItem
} from "@heroui/react"

export default function ProcessedPascosPage() {
    const navigate = useNavigate()
    const [searchQuery, setSearchQuery] = useState("")
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("")
    const [statusFilter, setStatusFilter] = useState<string>("")

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery)
        }, 300)

        return () => clearTimeout(timer)
    }, [searchQuery])

    // Fetch PASCOs with server-side filtering and pagination
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isFetching, error, refetch } = useListPascosPaginated({
        q: debouncedSearchQuery || undefined,
        status: statusFilter || undefined,
        limit: 100
    })

    // Flatten the paginated data
    const allPascos = data?.pages.flatMap((page: any) => page?.pascos || []) || []
    const isRefetching = isFetching && !isLoading

    const stats = {
        total: allPascos.length,
        level100: allPascos.filter((p) => p.level === 100).length,
        level200: allPascos.filter((p) => p.level === 200).length,
        level300: allPascos.filter((p) => p.level === 300).length,
        level400: allPascos.filter((p) => p.level === 400).length,
    }

    // Error state
    if (error && !data) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="mb-4 flex justify-center">
                        <div className="rounded-full bg-danger/10 p-3">
                            <FileText className="h-8 w-8 text-danger" />
                        </div>
                    </div>
                    <h3 className="mb-2 text-lg font-semibold text-danger">Failed to load PASCOs</h3>
                    <p className="mb-4 text-sm text-default-500">
                        {error?.message || "An error occurred while loading processed PASCOs."}
                    </p>
                    <Button
                        color="primary"
                        onPress={() => refetch()}
                        isLoading={isLoading}
                        startContent={!isLoading ? <RefreshCw className="h-4 w-4" /> : undefined}
                        className="rounded"
                    >
                        Try Again
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-default-50">
            {/* Header */}
            <header className="border-b border-border bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20 shadow-sm">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex h-auto sm:h-16 py-3 sm:py-0 items-center justify-between">
                        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                            <div className="flex h-8 sm:h-10 w-8 sm:w-10 items-center justify-center rounded-lg bg-gradient-to-br from-success to-success-600 shadow-md">
                                <CheckCircle2 className="h-4 sm:h-5 w-4 sm:w-5 text-success-foreground" />
                            </div>
                            <div>
                                <h1 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                    All PASCOs
                                </h1>
                                <p className="text-xs text-default-500 font-medium">
                                    {allPascos.length} total {isRefetching && '(updating...)'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="space-y-6">
                    {/* Stats Overview */}
                    <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
                        <Card className="border shadow-none border-success/20 rounded bg-gradient-to-br from-success/10 to-success/5">
                            <CardBody className="p-3 sm:p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs text-default-500 uppercase tracking-wide font-medium mb-1">
                                            Total Processed
                                        </p>
                                        <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                            {stats.total}
                                        </p>
                                    </div>
                                    <div className="hidden sm:flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-success to-success-600 shadow-md">
                                        <CheckCircle2 className="h-6 w-6 text-success-foreground" />
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                        <Card className="border shadow-none border-border rounded bg-secondary">
                            <CardBody className="p-3 sm:p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs text-default-500 uppercase tracking-wide font-medium mb-1">
                                            Level 100
                                        </p>
                                        <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                            {stats.level100}
                                        </p>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                        <Card className="border shadow-none border-border rounded bg-secondary">
                            <CardBody className="p-3 sm:p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs text-default-500 uppercase tracking-wide font-medium mb-1">
                                            Level 200
                                        </p>
                                        <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                            {stats.level200}
                                        </p>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                        <Card className="border shadow-none border-border rounded bg-secondary">
                            <CardBody className="p-3 sm:p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs text-default-500 uppercase tracking-wide font-medium mb-1">
                                            Level 300
                                        </p>
                                        <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                            {stats.level300}
                                        </p>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                        <Card className="border shadow-none border-border rounded bg-secondary">
                            <CardBody className="p-3 sm:p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs text-default-500 uppercase tracking-wide font-medium mb-1">
                                            Level 400
                                        </p>
                                        <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                            {stats.level400}
                                        </p>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </div>

                    {/* Info Banner */}
                    <Card className="border border-primary/20 rounded shadow-none bg-gradient-to-br from-primary/5 to-primary/10">
                        <CardBody className="p-4">
                            <div className="flex gap-3">
                                <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <h3 className="text-sm font-semibold text-foreground mb-1">All PASCOs</h3>
                                    <p className="text-xs text-default-600">
                                        View and manage all PASCOs in the system. Use filters to narrow down by processing status or search for specific courses.
                                    </p>
                                </div>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Search and Filters */}
                    <Card className="mb-6 bg-secondary rounded shadow-none border border-border">
                        <CardBody className="p-4">
                            <div className="flex flex-col gap-4">
                                {/* Search */}
                                <Input
                                    placeholder="Search PASCOs by course code or name..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    startContent={<Search className="h-4 w-4 text-default-400" />}
                                    className="w-full"
                                    classNames={{
                                        input: "text-sm",
                                        inputWrapper: "rounded"
                                    }}
                                />

                                {/* Status Filter */}
                                <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
                                    <Select
                                        label="Processing Status"
                                        placeholder="Select status"
                                        selectedKeys={statusFilter ? [statusFilter] : ["all"]}
                                        onSelectionChange={(keys) => {
                                            const selected = Array.from(keys)[0] as string
                                            setStatusFilter(selected === "all" ? "" : selected)
                                        }}
                                        className="w-full sm:w-48"
                                        classNames={{
                                            trigger: "shadow-none rounded border border-border",
                                            popoverContent: "rounded border border-border"
                                        }}
                                        size="sm"
                                    >
                                        <SelectItem key="all" className="rounded">All</SelectItem>
                                        <SelectItem key="pending" className="rounded">Pending</SelectItem>
                                        <SelectItem key="processing" className="rounded">Processing</SelectItem>
                                        <SelectItem key="completed" className="rounded">Completed</SelectItem>
                                        <SelectItem key="failed" className="rounded">Failed</SelectItem>
                                    </Select>
                                </div>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Table */}
                    {isLoading && !data ? (
                        <Card className="border border-border rounded shadow-none bg-secondary">
                            <CardBody className="p-8">
                                <div className="space-y-4">
                                    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                                        <div key={i} className="flex items-center gap-4 p-4 bg-default-100 rounded animate-pulse">
                                            <div className="h-12 w-12 bg-default-200 rounded" />
                                            <div className="flex-1 space-y-2">
                                                <div className="h-4 w-3/4 bg-default-200 rounded" />
                                                <div className="h-3 w-1/2 bg-default-200 rounded" />
                                            </div>
                                            <div className="h-6 w-20 bg-default-200 rounded" />
                                        </div>
                                    ))}
                                </div>
                            </CardBody>
                        </Card>
                    ) : allPascos.length > 0 ? (
                        <PascosTable
                            pascos={allPascos}
                            fetchNextPage={fetchNextPage}
                            hasNextPage={hasNextPage}
                            isFetchingNextPage={isFetchingNextPage}
                        />
                    ) : (
                        <Card className="border border-border rounded shadow-none bg-secondary">
                            <CardBody className="p-12 text-center">
                                <div className="flex justify-center mb-4">
                                    <div className="p-3 rounded-lg bg-default-100">
                                        <Search className="h-12 w-12 text-default-400" />
                                    </div>
                                </div>
                                <h3 className="text-sm font-semibold text-foreground mb-1">No PASCOs found</h3>
                                <p className="text-sm text-default-500 mb-4">
                                    {searchQuery || statusFilter
                                        ? "Try adjusting your filters or search query."
                                        : "There are no PASCOs yet."}
                                </p>
                                {!searchQuery && !statusFilter && (
                                    <Button
                                        variant="flat"
                                        onPress={() => navigate("/pascos/upload")}
                                        className="rounded"
                                    >
                                        Upload PASCO
                                    </Button>
                                )}
                            </CardBody>
                        </Card>
                    )}
                </div>
            </main>
        </div>
    )
}
